/**
 * User Management Page - Admin Only
 * Displays user list, stats, and provides admin controls
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { UserTable } from '../../components/admin/UserTable';
import { InviteUserDialog } from '../../components/admin/InviteUserDialog';
import { EditUserDialog } from '../../components/admin/EditUserDialog';
import { PageHeader } from '../../components/layout/PageHeader';
import { StatCard } from '../../components/ui/stat-card';
import { StatCardSkeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Users, UserCheck, UserPlus, Clock, Search, X, Loader2 } from 'lucide-react';
import type { User } from '../../types/api';
import {
  useUserListStore,
  useUserStatsStore,
  useApproveUser,
  useRejectUser,
  useDisableUser,
  useEnableUser,
  useDeleteUser,
  useResetPassword,
  useResendInvite,
} from '../../stores/users';
import { usePresenceStore } from '../../stores/presence';
import { useConnectionStore } from '../../stores/connection';

export function UserManagementPage() {
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const instanceId = useConnectionStore(s => s.instanceId);
  useUserStatsStore.live();
  const stats = useUserStatsStore(s => s.stats);
  usePresenceStore.live({ entity: "user" });
  const presence = usePresenceStore(s => s.presence);

  const filter = {
    ...(statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(roleFilter && roleFilter !== 'all' ? { role: roleFilter } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  };

  const storeSetFilter = useUserListStore(s => s.setFilter);
  useEffect(() => { storeSetFilter(filter); }, [filter, storeSetFilter]);
  useUserListStore.live();
  const usersResponse = useUserListStore(s => s.users);
  const isLoading = useUserListStore(s => s._loading);

  const approveUser = useApproveUser();
  const rejectUser = useRejectUser();
  const disableUser = useDisableUser();
  const enableUser = useEnableUser();
  const deleteUser = useDeleteUser();
  const resetPassword = useResetPassword();
  const resendInvite = useResendInvite();

  const selectedUser = usersResponse?.data.find(u => u.user_id === selectedUserId) ?? null;

  function handleEditUser(user: User) {
    setSelectedUserId(user.user_id);
    setEditDialogOpen(true);
  }

  async function handleResetPassword(userId: number) {
    if (confirm('Are you sure you want to reset this user\'s password? They will receive an email with a temporary password.')) {
      await resetPassword.mutate({ userId });
      toast.success('Password reset successfully. The user will receive an email with their temporary password.');
    }
  }

  async function handleResendInvite(userId: number) {
    if (confirm('Resend invitation email to this user?')) {
      await resendInvite.mutate({ userId, baseUrl: window.location.origin });
      toast.success('Invitation email resent successfully.');
    }
  }

  return (
    <>
      <PageHeader
        title="User Management"
        description="Manage user accounts and permissions"
        breadcrumbs={[
          { label: 'Administration', href: '#' },
          { label: 'Users' }
        ]}
        actions={
          <Button onClick={() => setInviteDialogOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite User
          </Button>
        }
      />

      <main className="flex-1 overflow-y-auto bg-muted/40">
        <div className="container mx-auto p-6 lg:p-8">
          <div className="flex flex-col gap-6">
            {/* Stats Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {stats ? (
                <>
                  <StatCard
                    title="Total Users"
                    value={stats.total}
                    icon={Users}
                    description="All user accounts"
                  />
                  <StatCard
                    title="Pending Approval"
                    value={stats.byStatus.pending_approval}
                    icon={Clock}
                    description="Awaiting review"
                  />
                  <StatCard
                    title="Active Users"
                    value={stats.byStatus.active}
                    icon={UserCheck}
                    description="Currently active"
                  />
                  <StatCard
                    title="Invited"
                    value={stats.byStatus.invited}
                    icon={UserPlus}
                    description="Pending registration"
                  />
                </>
              ) : (
                <>
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                </>
              )}
            </div>

            {/* Users Table */}
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle>Users</CardTitle>
                    {usersResponse && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {usersResponse.total} {usersResponse.total === 1 ? 'user' : 'users'} found
                        {(debouncedSearch || statusFilter !== 'all' || roleFilter !== 'all') && ' (filtered)'}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="relative w-full sm:w-64">
                      {isLoading && debouncedSearch ? (
                        <Loader2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                      ) : (
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      )}
                      <Input
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 pr-9"
                      />
                      {searchQuery && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                          onClick={() => setSearchQuery('')}
                        >
                          <X className="h-4 w-4" />
                          <span className="sr-only">Clear search</span>
                        </Button>
                      )}
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="pending_approval">Pending</SelectItem>
                        <SelectItem value="invited">Invited</SelectItem>
                        <SelectItem value="disabled">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="All Roles" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="Admin">Admin</SelectItem>
                        <SelectItem value="Editor">Editor</SelectItem>
                        <SelectItem value="Manager">Manager</SelectItem>
                        <SelectItem value="Viewer">Viewer</SelectItem>
                        <SelectItem value="External">External</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <UserTable
                  users={usersResponse?.data || []}
                  isLoading={isLoading}
                  presence={presence}
                  currentInstanceId={instanceId}
                  onEdit={handleEditUser}
                  onResetPassword={handleResetPassword}
                  onResendInvite={handleResendInvite}
                  onApprove={(userId) => approveUser.mutate({ userId })}
                  onReject={(userId) => rejectUser.mutate({ userId })}
                  onDisable={(userId) => disableUser.mutate({ userId })}
                  onEnable={(userId) => enableUser.mutate({ userId })}
                  onDelete={(userId) => {
                    if (confirm('Are you sure you want to delete this user?')) {
                      deleteUser.mutate({ userId });
                    }
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
      />

      <EditUserDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        user={selectedUser}
      />
    </>
  );
}
